import React, { useState, useEffect } from 'react';
import { Campaign } from '@restore/services-lib';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import MultiselectDropdown, { MultiselectDropdownProps } from '../MultiselectDropdown';
import { CampaignsContextProvider, useCampaignsContext } from 'pages/Campaigns/campaigns.context';

interface CampaignSelectDropdownInterface {
  selectedCampaigns?: Campaign[];
  selectedCampaignIds?: number[];
  onChange: (selectedOptions: DropdownOption[]) => void;
  containedLabel?: boolean;
  inputProps?: Omit<MultiselectDropdownProps['inputProps'], 'sx'>;
}

const getSelectedOptionsByCampaigns = (campaigns: Campaign[]) =>
  campaigns?.map((campaign: Campaign) => ({ id: campaign.id, name: campaign.name }));

const CampaignSelectDropdownComponent = ({
  selectedCampaigns,
  selectedCampaignIds,
  onChange,
  containedLabel,
  inputProps,
}: CampaignSelectDropdownInterface) => {
  const { campaigns: availableCampaigns, getCampaignDropdownSelectOptionsByIds } =
    useCampaignsContext();

  const getSelectedOptions = () => {
    if (selectedCampaigns) {
      return getSelectedOptionsByCampaigns(selectedCampaigns);
    } else if (availableCampaigns && selectedCampaignIds) {
      return getCampaignDropdownSelectOptionsByIds(selectedCampaignIds) ?? [];
    } else return [];
  };

  const [selectedOptions, setSelectedOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    setSelectedOptions(getSelectedOptions());
  }, [selectedCampaigns, selectedCampaignIds, availableCampaigns]);

  const campaignsOptions: DropdownOption[] = availableCampaigns.map((campaign) => ({
    name: campaign.name,
    id: campaign.id,
  }));

  const handleChange = (newSelectedOptions: DropdownOption[]) => {
    setSelectedOptions(newSelectedOptions);
    onChange(newSelectedOptions);
  };

  return (
    <MultiselectDropdown
      fieldLabel='Campaigns'
      allOptions={campaignsOptions}
      fullWidth={true}
      selected={selectedOptions}
      setSelected={(selectedOptions) => handleChange(selectedOptions)}
      variant='outlined'
      containedLabel={containedLabel}
      inputProps={inputProps}
    />
  );
};

const CampaignSelectDropdown = ({
  selectedCampaigns,
  selectedCampaignIds,
  onChange,
  containedLabel,
  inputProps,
}: CampaignSelectDropdownInterface) => (
  <CampaignsContextProvider>
    <CampaignSelectDropdownComponent
      selectedCampaigns={selectedCampaigns}
      selectedCampaignIds={selectedCampaignIds}
      onChange={onChange}
      containedLabel={containedLabel}
      inputProps={inputProps}
    />
  </CampaignsContextProvider>
);

export default CampaignSelectDropdown;
